<template>
  <section class="fg-search">
    <div class="fg-search__direction">
      <div class="fg-seg" role="radiogroup">
        <label class="fg-seg__opt" :class="{ 'is-active': store.search.direction === Direction.Return }">
          <input v-model="store.search.direction" type="radio" :value="Direction.Return" />
          <span>{{ t('search.directionRoundTrip') }}</span>
        </label>
        <label class="fg-seg__opt" :class="{ 'is-active': store.search.direction === Direction.Outward }">
          <input v-model="store.search.direction" type="radio" :value="Direction.Outward" />
          <span>{{ t('search.directionOneWay') }}</span>
        </label>
      </div>
    </div>

    <div class="fg-search__cards">
      <button
        v-for="card in cards"
        :key="card.id"
        type="button"
        class="fg-search__card"
        :disabled="card.id === 'journey' && store.loadingJourney"
        @click="openDrawer = card.id"
      >
        <span class="fg-search__card-icon"><Icon :name="card.icon" /></span>
        <span class="fg-search__card-text">
          <span class="fg-search__card-title">{{ t(card.titleKey) }}</span>
          <span class="fg-search__card-value">{{ card.value }}</span>
        </span>
        <span v-if="card.id === 'journey' && store.loadingJourney" class="fg-search__card-loader">
          <svg class="fg-spinner__svg" viewBox="0 0 50 50" width="16" height="16">
            <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="4" />
          </svg>
        </span>
        <span v-else class="fg-search__card-chev"><Icon name="chevron-right" :size="16" /></span>
      </button>
    </div>

    <div class="fg-search__cta">
      <button class="fg-btn fg-btn--primary fg-btn--lg" :disabled="!ready || store.loading || store.loadingJourney" @click="submit">
        <Icon v-if="!store.loading" name="search" :size="18" />
        <span>{{ store.loading ? t('search.submitting') : t('search.submit') }}</span>
      </button>
    </div>

    <JourneysDrawer :open="openDrawer === 'journey'" @close="openDrawer = null" @next="openDrawer = 'calendar'" />
    <CalendarDrawer :open="openDrawer === 'calendar'" @close="openDrawer = null" />
    <PassengersDrawer :open="openDrawer === 'passengers'" @close="openDrawer = null" />
    <VehicleDrawer :open="openDrawer === 'vehicle'" @close="openDrawer = null" />
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useStore } from '../store'
import { useI18n } from '../i18n'
import { useSearch } from '../composables/useSearch'
import { useSearchSummary } from '../composables/useSearchSummary'
import { isReadyForSearch } from '../../../shared/rules'
import { Direction } from '../../../shared/types/enums'
import Icon from './Icon.vue'
import JourneysDrawer from './drawers/JourneysDrawer.vue'
import CalendarDrawer from './drawers/CalendarDrawer.vue'
import PassengersDrawer from './drawers/PassengersDrawer.vue'
import VehicleDrawer from './drawers/VehicleDrawer.vue'

type DrawerName = 'journey' | 'calendar' | 'passengers' | 'vehicle' | null
type IconName = 'ship' | 'calendar' | 'users' | 'car' | 'bike' | 'van' | 'moto' | 'walker'

const VEHICLE_ICONS: Record<string, IconName> = {
  NoCar: 'walker', Bike: 'bike', Car: 'car', Van: 'van', Moto: 'moto',
}

const store = useStore()
const search = useSearch()
const { t } = useI18n()
const { journeySummary, datesSummary, passengersSummary, vehicleSummary } = useSearchSummary()

const openDrawer = ref<DrawerName>(null)
const ready = computed(() => isReadyForSearch(store.search))

const cards = computed(() => [
  { id: 'journey' as const,    icon: 'ship' as IconName,     titleKey: 'search.journeyCard',    value: journeySummary.value },
  { id: 'calendar' as const,   icon: 'calendar' as IconName, titleKey: 'search.datesCard',      value: datesSummary.value },
  { id: 'passengers' as const, icon: 'users' as IconName,    titleKey: 'search.passengersCard', value: passengersSummary.value },
  { id: 'vehicle' as const,    icon: vehicleIcon.value,      titleKey: 'search.vehicleCard',    value: vehicleSummary.value },
])

const vehicleIcon = computed<IconName>(() => VEHICLE_ICONS[store.search.vehicle.type] ?? 'car')

// Délègue au composable useSearch (navigation optimiste + skeleton sur Results).
function submit(): void {
  if (!ready.value) return
  void search.run()
}

</script>
